import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { trpc } from "../client";
import { AUTH_TOKEN_EVENT, getToken } from "../../auth/utils";

export type UserProfile = {
  firstName?: string;
  lastName?: string;
};

export const useCurrentUser = () => {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  useEffect(() => {
    const handleTokenChange = () => {
      setTokenState(getToken());
    };

    window.addEventListener(AUTH_TOKEN_EVENT, handleTokenChange);
    window.addEventListener("storage", handleTokenChange);

    return () => {
      window.removeEventListener(AUTH_TOKEN_EVENT, handleTokenChange);
      window.removeEventListener("storage", handleTokenChange);
    };
  }, []);

  return trpc.authRouter.getCurrentUser.useQuery(undefined, {
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: false,
    refetchOnWindowFocus: true,
    enabled: token != null && token !== "",
  });
};

export const useLogout = () => {
  const utils = trpc.useUtils();

  return trpc.authRouter.logout.useMutation({
    onSuccess: () => {
      void utils.invalidate();
    },
  });
};

export const useWorkosAuthorizationUrl = () => {
  return trpc.authRouter.getWorkosAuthorizationUrl.useMutation();
};

export const useInvalidateAuthQueries = () => {
  const utils = trpc.useUtils();

  return useCallback(async () => {
    await Promise.all([
      utils.authRouter.getCurrentUser.invalidate(),
      utils.authRouter.getProfile.invalidate(),
    ]);
  }, [utils]);
};

export const usePasswordWorkosSignIn = () => {
  return trpc.authRouter.passwordWorkosSignIn.useMutation();
};

export const useCreateWorkosMagicAuth = () => {
  return trpc.authRouter.createWorkosMagicAuth.useMutation();
};

export const useRequestWorkosPasswordReset = () => {
  return trpc.authRouter.requestWorkosPasswordReset.useMutation();
};

export const useResetWorkosPassword = () => {
  return trpc.authRouter.resetWorkosPassword.useMutation();
};

export const useAuthenticateWorkosMagicAuth = () => {
  return trpc.authRouter.authenticateWorkosMagicAuth.useMutation();
};

export const useAuthenticateWorkosEmailVerification = () => {
  return trpc.authRouter.authenticateWorkosEmailVerification.useMutation();
};

export const useAuthenticateWorkosTotp = () => {
  return trpc.authRouter.authenticateWorkosTotp.useMutation();
};

export const useAuthenticateWorkosOrganizationSelection = () => {
  return trpc.authRouter.authenticateWorkosOrganizationSelection.useMutation();
};

export const useExchangeWorkosCode = () => {
  return trpc.authRouter.exchangeWorkosCode.useMutation({
    retry: false,
  });
};

function subscribeWidgetSession(onChange: () => void): () => void {
  window.addEventListener(AUTH_TOKEN_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(AUTH_TOKEN_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export const useWorkosUsersManagementWidgetToken = (options?: {
  enabled?: boolean;
}) => {
  const sessionToken = useSyncExternalStore(subscribeWidgetSession, getToken);
  const { client } = trpc.useUtils();
  const enabled = options?.enabled ?? true;
  const [result, setResult] = useState<{
    sessionToken: string;
    data?: { token: string };
    error?: unknown;
  }>();

  useEffect(() => {
    if (!enabled || sessionToken === null) return;
    let active = true;
    // Widget credentials must never enter the shared query cache. Keep the
    // response bound to the session that requested it, including late replies.
    void client.authRouter.getWorkosUsersManagementWidgetToken.query().then(
      (data) => {
        if (active && getToken() === sessionToken) {
          setResult({ sessionToken, data });
        }
      },
      (error: unknown) => {
        if (active && getToken() === sessionToken) {
          setResult({ sessionToken, error });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [client, enabled, sessionToken]);

  const current =
    enabled && result?.sessionToken === sessionToken ? result : undefined;
  return {
    data: current?.data,
    error: current?.error,
    isLoading: enabled && sessionToken !== null && current === undefined,
  };
};

export const useUpdateProfile = () => {
  const utils = trpc.useUtils();

  return trpc.authRouter.updateProfile.useMutation({
    onSuccess: () => {
      void utils.authRouter.getCurrentUser.invalidate();
      void utils.authRouter.getProfile.invalidate();
    },
  });
};
